// ============================================================================
// WorkflowDefinitionsScreen — list/CRUD of workflow definitions.
// ============================================================================

import React, { useCallback, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import { SyutGrid, type SyutGridColumn, type ActionMenuItem } from 'components/ui';
import notificationService from 'services/notificationService';
import { useWorkflowDefinitions } from '../hooks/useWorkflowDefinitions';
import { workflowService } from '../api/workflowService';
import type { CreateDefinitionRequest, WorkflowDefinition } from '../api/types';
import WorkflowDesigner from '../components/WorkflowDesigner';

const WorkflowDefinitionsScreen: React.FC = () => {
  const { data, loading, refetch } = useWorkflowDefinitions(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [designingId, setDesigningId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<WorkflowDefinition | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleEdit = useCallback((d: WorkflowDefinition) => setDesigningId(d.id), []);

  const columns: SyutGridColumn<WorkflowDefinition>[] = useMemo(() => [
    { id: 'definitionCode', header: 'Code', accessorKey: 'definitionCode', size: 200, enableSorting: true },
    { id: 'version', header: 'Version', accessorKey: 'version', size: 80, enableSorting: true },
    { id: 'displayName', header: 'Display name', accessorKey: 'displayName', size: 240, enableSorting: true },
    { id: 'entityType', header: 'Entity', accessorKey: 'entityType', size: 140, enableSorting: true },
    {
      id: 'isPublished',
      header: 'Published',
      size: 110,
      cell: ({ row }) => row.original.isPublished
        ? <span style={{ color: '#16a34a', fontWeight: 600 }}>Published</span>
        : <span style={{ color: '#d97706', fontWeight: 600 }}>Draft</span>,
    },
  ], []);

  const getRowActions = useCallback((d: WorkflowDefinition): ActionMenuItem[] => [
    { id: 'edit', label: 'Open designer', icon: <IconSystem.edit size={16} />, onClick: () => handleEdit(d) },
    { id: 'delete', label: 'Delete', icon: <IconSystem.trash size={16} />, onClick: () => setDeleteTarget(d), variant: 'danger' },
  ], [handleEdit]);

  const actionButtons: ActionButton[] = useMemo(() => [
    { id: 'add', icon: <IconSystem.add size={18} />, label: 'New definition', tooltip: 'Create draft definition',
      onClick: () => setCreateOpen(true), variant: 'icon' },
    { id: 'refresh', icon: <IconSystem.refresh size={18} />, label: 'Refresh',
      onClick: () => refetch(), variant: 'icon' },
  ], [refetch]);

  if (designingId) {
    return (
      <div className="module-master-container">
        <ModuleHeader
          title="Workflow Designer"
          description="Edit states, transitions, actions, and spawn rules for the selected definition."
          icon={<IconSystem.link size={18} />}
          actions={[
            { id: 'back', icon: <IconSystem.arrowLeft size={18} />, label: 'Back', tooltip: 'Back to definitions',
              onClick: () => { setDesigningId(null); refetch(); }, variant: 'icon' },
          ]}
        />
        <div className="module-content" style={{ marginTop: '6px', overflow: 'auto' }}>
          <WorkflowDesigner definitionId={designingId} />
        </div>
      </div>
    );
  }

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Workflow Definitions"
        description="Create, edit, and publish workflow definitions for any entity type."
        icon={<IconSystem.link size={18} />}
        actions={actionButtons}
      />
      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <SyutGrid<WorkflowDefinition>
              data={data}
              columns={columns}
              enableSorting
              enablePagination={false}
              rowActions={getRowActions}
              isLoading={loading}
              emptyMessage="No definitions. Click 'New definition' to start."
            />
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Delete definition"
        message={deleteTarget
          ? `Delete '${deleteTarget.definitionCode}/v${deleteTarget.version}'? Existing instances are preserved but new instances cannot be started.`
          : ''}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={isDeleting}
        loadingMessage="Deleting…"
        onConfirm={async () => {
          if (!deleteTarget) return;
          setIsDeleting(true);
          try {
            await workflowService.deleteDefinition(deleteTarget.id);
            notificationService.toast.success(`Deleted '${deleteTarget.definitionCode}'.`);
            setDeleteTarget(null);
            await refetch();
          } catch (e: any) {
            notificationService.toast.error(e?.response?.data?.message ?? 'Delete failed.');
          } finally {
            setIsDeleting(false);
          }
        }}
        onCancel={() => setDeleteTarget(null)}
      />

      {createOpen && (
        <CreateDefinitionModal
          onClose={() => setCreateOpen(false)}
          onSaved={(id) => { setCreateOpen(false); refetch(); setDesigningId(id); }}
        />
      )}
    </div>
  );
};

const CreateDefinitionModal: React.FC<{
  onClose: () => void;
  onSaved: (id: string) => void;
}> = ({ onClose, onSaved }) => {
  const [form, setForm] = useState<CreateDefinitionRequest>({
    definitionCode: '',
    version: 1,
    displayName: '',
    entityType: '',
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!form.definitionCode || !form.displayName || !form.entityType) {
      notificationService.toast.warning('Code, display name, and entity type are required.');
      return;
    }
    setBusy(true);
    try {
      const def = await workflowService.createDefinition(form);
      notificationService.toast.success('Definition created (draft).');
      onSaved(def.id);
    } catch (e: any) {
      notificationService.toast.error(e?.response?.data?.message ?? 'Create failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md">
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">New workflow definition</h2>
          <button className="modal-close" onClick={onClose}><IconSystem.x size={20} /></button>
        </div>
        <div className="modal-content">
          <div className="form-group">
            <label className="form-label">Definition code (kebab-case) *</label>
            <input className="form-input" value={form.definitionCode}
              onChange={(e) => setForm({ ...form, definitionCode: e.target.value.toLowerCase() })}
              placeholder="e.g. arbitration-case" />
          </div>
          <div className="form-grid-2">
            <div className="form-group">
              <label className="form-label">Version *</label>
              <input className="form-input" type="number" min={1} value={form.version}
                onChange={(e) => setForm({ ...form, version: Number(e.target.value) || 1 })} />
            </div>
            <div className="form-group">
              <label className="form-label">Entity type *</label>
              <input className="form-input" value={form.entityType}
                onChange={(e) => setForm({ ...form, entityType: e.target.value })}
                placeholder="e.g. Case, PurchaseOrder" />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Display name *</label>
            <input className="form-input" value={form.displayName}
              onChange={(e) => setForm({ ...form, displayName: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea className="form-textarea" rows={3} value={form.description ?? ''}
              onChange={(e) => setForm({ ...form, description: e.target.value || undefined })} />
          </div>
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save & open'}</button>
        </div>
      </div>
    </>
  );
};

export default WorkflowDefinitionsScreen;
